import {
  useSendApiErrorNotification,
  useSendNotification,
} from "@app/hooks/useNotification";
import { useFeatureFlags } from "@app/lib/auth/AuthContext";
import { clientFetch } from "@app/lib/egress/client";
import {
  getErrorFromResponse,
  useFetcher,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type { GetOrPostManagedDataSourceConfigResponseBody } from "@app/types/api/data_sources/managed_config";
import type { GetDataSourcePermissionsResponseBody } from "@app/types/api/data_sources/managed_permissions";
import type {
  ConnectorPermission,
  ContentNode,
} from "@app/types/connectors/connectors_api";
import type {
  ContentNodesViewType,
  FetchChildResourcesError,
} from "@app/types/connectors/content_nodes";
import type { DataSourceType } from "@app/types/data_source";
import type { APIError } from "@app/types/error";
import { isAPIError } from "@app/types/error";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback, useMemo, useState } from "react";
import type { Fetcher } from "swr";
import useSWRMutation from "swr/mutation";

interface UseConnectorPermissionsReturn<T extends ConnectorPermission | null> {
  resources: T extends ConnectorPermission
    ? GetDataSourcePermissionsResponseBody<T>["resources"]
    : ContentNode[];
  isResourcesLoading: boolean;
  isResourcesError: boolean;
  resourcesError: APIError | null;
}

function getConnectorPermissionsUrl({
  owner,
  dataSource,
  parentId,
  filterPermission,
  viewType,
}: {
  owner: LightWorkspaceType;
  dataSource: DataSourceType;
  parentId: string | null;
  filterPermission?: ConnectorPermission | null;
  viewType?: ContentNodesViewType;
}): string {
  let url = `/api/w/${owner.sId}/data_sources/${dataSource.sId}/managed/permissions?viewType=${viewType}`;
  if (parentId) {
    url += `&parentId=${parentId}`;
  }
  if (filterPermission) {
    url += `&filterPermission=${filterPermission}`;
  }
  return url;
}

/**
 * @cc [owner:frankaloia,label:error-handling;product] child-permission-auth-is-resource-scoped
 * Authorization and missing-document errors from a child permissions request
 * MUST be classified as `resource_inaccessible` so bulk selection can skip that
 * branch. Other errors MUST remain fatal.
 */
function getFetchChildResourcesError(error: unknown): FetchChildResourcesError {
  const isResourceInaccessible =
    isAPIError(error) &&
    (error.type === "data_source_auth_error" ||
      error.type === "data_source_document_not_found");

  return {
    error: normalizeError(error),
    type: isResourceInaccessible ? "resource_inaccessible" : "fatal",
  };
}

export function useConnectorPermissions<T extends ConnectorPermission | null>({
  owner,
  dataSource,
  parentId,
  filterPermission,
  disabled,
  viewType,
}: {
  owner: LightWorkspaceType;
  dataSource: DataSourceType;
  parentId: string | null;
  filterPermission: T;
  disabled?: boolean;
  viewType?: ContentNodesViewType;
}): UseConnectorPermissionsReturn<T> {
  const { fetcher } = useFetcher();
  const { featureFlags } = useFeatureFlags();
  const permissionsFetcher: Fetcher<
    T extends ConnectorPermission
      ? GetDataSourcePermissionsResponseBody<T>
      : GetDataSourcePermissionsResponseBody
  > = fetcher;

  const url = getConnectorPermissionsUrl({
    owner,
    dataSource,
    parentId,
    filterPermission,
    viewType,
  });

  const { data, error } = useSWRWithDefaults(url, permissionsFetcher, {
    disabled,
  });

  return {
    resources: useMemo(
      () =>
        data
          ? data.resources.filter(
              (resource) =>
                resource.providerVisibility !== "private" ||
                featureFlags.includes("index_private_slack_channel")
            )
          : [],
      [data, featureFlags]
    ),
    isResourcesLoading: !error && !data,
    isResourcesError: error,
    resourcesError: error ? (error.error as APIError) : null,
  } as UseConnectorPermissionsReturn<T>;
}

export function useFetchConnectorPermissions({
  owner,
  dataSource,
  viewType = "all",
}: {
  owner: LightWorkspaceType;
  dataSource: DataSourceType;
  viewType?: ContentNodesViewType;
}): (
  parentId: string
) => Promise<Result<ContentNode[], FetchChildResourcesError>> {
  const { featureFlags } = useFeatureFlags();
  const { trigger } = useSWRMutation(
    getConnectorPermissionsUrl({
      owner,
      dataSource,
      parentId: null,
      viewType,
    }),
    async (_key: string, { arg: parentId }: { arg: string }) => {
      const response = await clientFetch(
        getConnectorPermissionsUrl({
          owner,
          dataSource,
          parentId,
          viewType,
        })
      );
      if (!response.ok) {
        const error = await getErrorFromResponse(response);
        return new Err(getFetchChildResourcesError(error));
      }

      const data: GetDataSourcePermissionsResponseBody = await response.json();
      return new Ok(
        data.resources.filter(
          (resource) =>
            resource.providerVisibility !== "private" ||
            featureFlags.includes("index_private_slack_channel")
        )
      );
    }
  );

  return useCallback(async (parentId: string) => trigger(parentId), [trigger]);
}

export function useConnectorConfig({
  configKey,
  dataSource,
  disabled,
  owner,
}: {
  configKey: string;
  dataSource: DataSourceType | null;
  disabled?: boolean;
  owner: LightWorkspaceType;
}) {
  const { fetcher } = useFetcher();
  const configFetcher: Fetcher<GetOrPostManagedDataSourceConfigResponseBody> =
    fetcher;

  const url = `/api/w/${owner.sId}/data_sources/${dataSource?.sId}/managed/config/${configKey}`;

  const { data, error, mutate } = useSWRWithDefaults(url, configFetcher, {
    disabled: disabled || !dataSource,
  });

  return {
    configValue: data ? data.configValue : null,
    isResourcesLoading: !error && !data,
    isResourcesError: error,
    mutateConfig: mutate,
  };
}

export function useOAuthMetadata({
  dataSource,
  disabled,
  owner,
}: {
  dataSource: DataSourceType | null;
  disabled?: boolean;
  owner: LightWorkspaceType;
}) {
  const { fetcher } = useFetcher();
  const metadataFetcher: Fetcher<{ metadata: Record<string, unknown> }> =
    fetcher;

  const url = `/api/w/${owner.sId}/data_sources/${dataSource?.sId}/managed/oauth-metadata`;

  const { data, error } = useSWRWithDefaults(url, metadataFetcher, {
    disabled: disabled || !dataSource,
  });

  return {
    metadata: data ? data.metadata : null,
    isMetadataLoading: !error && !data,
    isMetadataError: error,
  };
}

export function useToggleChatBot({
  dataSource,
  owner,
  botName,
}: {
  dataSource: DataSourceType | null;
  owner: LightWorkspaceType;
  botName: string;
}) {
  const sendApiErrorNotification = useSendApiErrorNotification();
  const sendNotification = useSendNotification();

  const { mutateConfig } = useConnectorConfig({
    owner,
    dataSource,
    configKey: "botEnabled",
    disabled: true, // Needed just to mutate
  });

  if (!dataSource) {
    return () => {
      sendNotification({
        type: "error",
        title: `Failed to Enable ${botName}`,
        description: `Tried to enable ${botName}, but no data source was found.`,
      });
    };
  }

  const doToggle = async (botEnabled: boolean) => {
    const res = await clientFetch(
      `/api/w/${owner.sId}/data_sources/${dataSource.sId}/managed/config/botEnabled`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({ configValue: botEnabled.toString() }),
      }
    );

    if (res.ok) {
      void mutateConfig();

      const response: GetOrPostManagedDataSourceConfigResponseBody =
        await res.json();

      const { configValue } = response;

      sendNotification({
        type: "success",
        title: botEnabled
          ? `${botName} Enabled Successfully`
          : `${botName} Disabled Successfully`,
        description: botEnabled
          ? `The ${botName} is now active and ready to use.`
          : `The ${botName} has been disabled.`,
      });
      return configValue;
    } else {
      const errorData = await getErrorFromResponse(res);

      sendApiErrorNotification({
        title: `Failed to Enable ${botName}`,
        error: errorData,
      });
      return null;
    }
  };

  return doToggle;
}

export function useTogglePdfEnabled({
  dataSource,
  owner,
}: {
  dataSource: DataSourceType | null;
  owner: LightWorkspaceType;
}) {
  const sendApiErrorNotification = useSendApiErrorNotification();
  const sendNotification = useSendNotification();
  const [isLoading, setIsLoading] = useState(false);

  const { mutateConfig } = useConnectorConfig({
    owner,
    dataSource,
    configKey: "pdfEnabled",
    disabled: true, // Needed just to mutate
  });

  if (!dataSource) {
    return {
      doToggle: () => {
        sendNotification({
          type: "error",
          title: "Failed to update PDF sync setting",
          description:
            "Tried to update PDF sync setting, but no data source was found.",
        });
      },
      isLoading: false,
    };
  }

  const doToggle = async (pdfEnabled: boolean) => {
    setIsLoading(true);
    const res = await clientFetch(
      `/api/w/${owner.sId}/data_sources/${dataSource.sId}/managed/config/pdfEnabled`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({ configValue: pdfEnabled.toString() }),
      }
    );

    if (res.ok) {
      void mutateConfig();

      const response: GetOrPostManagedDataSourceConfigResponseBody =
        await res.json();

      const { configValue } = response;

      sendNotification({
        type: "success",
        title: "PDF sync setting updated successfully",
        description: pdfEnabled
          ? "PDF syncing is now enabled."
          : "PDF syncing has been disabled.",
      });
      setIsLoading(false);
      return configValue;
    } else {
      const errorData = await getErrorFromResponse(res);

      sendApiErrorNotification({
        title: "Failed to update PDF sync setting",
        error: errorData,
      });
      setIsLoading(false);
      return null;
    }
  };

  return { doToggle, isLoading };
}
